
import { Template } from "@/types/whatsApp";

const WASSI_API_URL = "https://api.wassi.chat";
const WASSI_API_KEY = "23257599add7ae89a708a6ccd4a90cb720eee80098e96a1ef4b73293bdbe7d5c55a76402569376b1";

// Fetch templates from wassi.chat
export const getTemplates = async (): Promise<Template[]> => {
  try {
    const response = await fetch(`${WASSI_API_URL}/api/templates`, {
      method: 'GET',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${WASSI_API_KEY}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`Error fetching templates: ${response.status}`);
    }
    
    const data = await response.json();
    return data.templates || mockTemplates;
  } catch (error) {
    console.error('Error fetching templates:', error);
    // Return mock templates if API call fails
    return mockTemplates;
  }
};

// Create a new template on wassi.chat
export const createTemplate = async (template: Omit<Template, 'id' | 'createdAt' | 'updatedAt'>): Promise<Template> => {
  try {
    const response = await fetch(`${WASSI_API_URL}/api/templates`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${WASSI_API_KEY}`
      },
      body: JSON.stringify(template)
    });
    
    if (!response.ok) {
      throw new Error(`Error creating template: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error creating template:', error);
    // Return mock response for development
    return {
      ...template,
      id: Math.random().toString(36).substring(2, 9),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
  }
};

// Update an existing template
export const updateTemplate = async (template: Template): Promise<Template> => {
  try {
    const response = await fetch(`${WASSI_API_URL}/api/templates/${template.id}`, {
      method: 'PUT',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${WASSI_API_KEY}`
      },
      body: JSON.stringify(template)
    });
    
    if (!response.ok) {
      throw new Error(`Error updating template: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error updating template:', error);
    // Return mock response for development
    return {
      ...template,
      updatedAt: new Date().toISOString()
    };
  }
};

// Delete a template
export const deleteTemplate = async (id: string): Promise<boolean> => {
  try {
    const response = await fetch(`${WASSI_API_URL}/api/templates/${id}`, {
      method: 'DELETE',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${WASSI_API_KEY}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`Error deleting template: ${response.status}`);
    }
    
    return true;
  } catch (error) {
    console.error('Error deleting template:', error);
    return true; // Return success for development
  }
};

// Mock templates for development and fallback
const mockTemplates: Template[] = [
  {
    id: '1',
    name: 'Begrüßung',
    content: 'Hallo {{name}}, vielen Dank für Ihr Interesse. Wir freuen uns auf unser Gespräch!',
    variables: ['name'],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: '2',
    name: 'Terminbestätigung',
    content: 'Sehr geehrte(r) {{name}}, hiermit bestätigen wir Ihren Termin am {{termin}} in {{ort}}.',
    variables: ['name', 'termin', 'ort'],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: '3',
    name: 'Nicht erreicht',
    content: 'Hallo {{name}}, wir haben versucht, Sie telefonisch zu erreichen. Bitte rufen Sie uns zurück unter {{telefonnummer}}.',
    variables: ['name', 'telefonnummer'],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: '4',
    name: 'Angebot',
    content: 'Sehr geehrte(r) {{name}}, vielen Dank für Ihr Interesse. Anbei senden wir Ihnen unser Angebot.',
    variables: ['name'],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: '5',
    name: 'Nachfrage',
    content: 'Hallo {{name}}, haben Sie noch Fragen zu unserem Angebot oder Interesse an einem persönlichen Gespräch?',
    variables: ['name'],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: '6',
    name: 'Termin nicht eingehalten',
    content: 'Sehr geehrte(r) {{name}}, leider haben Sie den vereinbarten Termin am {{termin}} nicht wahrgenommen. Bitte kontaktieren Sie uns unter {{telefonnummer}} für einen neuen Termin.',
    variables: ['name', 'termin', 'telefonnummer'],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: '7',
    name: 'Terminerinnerung',
    content: 'Guten Tag {{name}}, wir möchten Sie an Ihren Termin morgen um {{uhrzeit}} erinnern. Wir freuen uns auf Sie!',
    variables: ['name', 'uhrzeit'],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: '8',
    name: 'Terminänderung',
    content: 'Sehr geehrte(r) {{name}}, leider müssen wir Ihren Termin am {{alter_termin}} verschieben. Können wir einen neuen Termin für {{neuer_termin}} vereinbaren?',
    variables: ['name', 'alter_termin', 'neuer_termin'],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: '9',
    name: 'Dankeschön',
    content: 'Vielen Dank für Ihren Besuch, {{name}}! Wir hoffen, Sie waren zufrieden und freuen uns auf Ihren nächsten Besuch.',
    variables: ['name'],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }
];

// Zapier webhook functionality
export const getZapierWebhook = (): string => {
  return localStorage.getItem('zapier_webhook_url') || '';
};

export const saveZapierWebhook = (url: string): void => {
  localStorage.setItem('zapier_webhook_url', url);
};

export const notifyZapierWebhook = async (templateId: string, data: Record<string, string>): Promise<boolean> => {
  const webhookUrl = getZapierWebhook();
  
  if (!webhookUrl) {
    throw new Error('No Zapier webhook URL configured');
  }
  
  try {
    const templates = await getTemplates();
    const template = templates.find(t => t.id === templateId);
    
    if (!template) {
      throw new Error(`Template with ID ${templateId} not found`);
    }
    
    let message = template.content;
    
    // Replace all variables in the template
    Object.entries(data).forEach(([key, value]) => {
      message = message.replace(new RegExp(`{{${key}}}`, 'g'), value);
    });
    
    // Send to Zapier webhook
    const response = await fetch(webhookUrl, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        template_id: templateId,
        template_name: template.name,
        message,
        data,
        timestamp: new Date().toISOString()
      }),
      mode: 'no-cors' // Add this to handle CORS
    });
    
    return true;
  } catch (error) {
    console.error('Error notifying Zapier webhook:', error);
    throw error;
  }
};

// Register a new customer with wassi.chat
export const registerCustomer = async (
  phone: string, 
  name: string, 
  welcomeMessage?: string
): Promise<boolean> => {
  try {
    const response = await fetch(`${WASSI_API_URL}/api/customer/register`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${WASSI_API_KEY}`
      },
      body: JSON.stringify({
        phone,
        name,
        welcomeMessage
      })
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Fehler beim Registrieren des Kunden: ${response.status} - ${errorText}`);
    }
    
    return true;
  } catch (error) {
    console.error('Error registering customer:', error);
    throw error;
  }
};
